import React from 'react';
import { Section } from '../Styled';
import { useTraining } from '../TrainingContext';
import { Text } from '@teachstonellc/component-library';
import i18n from 'helpers/i18n';
import messages from './messages';
import RichTextEditor from 'components/RichTextEditor';
import { isEmpty, isNil } from 'ramda';
import { isRichTextEmpty } from './utils';
import Location from '../Location';
import Sessions from '../Sessions';
import ResponsiveContent from 'components/ResponsiveContent';
import ReliabilityReport from './ReliabilityReport';
import TransitionReport from './ReliabilityReport/TransitionReport';
import usePermissions from 'hooks/usePermissions';
import useCurrentUser from 'hooks/useCurrentUser';
import CertificateSection from './CertificateSection';
import CoursesSection from './CoursesSection';

const sectionTitleClassName = 'block mb-2 text-base font-bold text-[#00779E]';

function Details() {
  const { training, template } = useTraining();
  const currentUser = useCurrentUser();
  const permissions = usePermissions('trainer_dashboard', 'trainings');

  function displayVirtualTrainingLink() {
    return (
      !isNil(training.virtual_training_link) &&
      !isEmpty(training.virtual_training_link)
    );
  }

  function displayDescription() {
    return !isRichTextEmpty(template.description);
  }

  function displayNotesTitle() {
    return !isNil(training.notes_title) && !isEmpty(training.notes_title);
  }

  function displayNotesDescription() {
    return !isRichTextEmpty(training.notes);
  }

  function displayNotesSection() {
    return displayNotesTitle() || displayNotesDescription();
  }

  function displayTestReport() {
    const issuesTest =
      training.training_template.reliability_test ||
      training.training_template.knowledge_test;

    const isObserverTransition =
      training.training_template.training_type === 'transition_observer';
    const isTrainerTransition =
      training.training_template.training_type === 'transition_trainer';
    const canShowSingleReport =
      issuesTest &&
      !isTrainerTransition &&
      (!isObserverTransition || Boolean(training.test_type));

    return (
      training.status === 'completed' &&
      (training.combined_test_report
        ? issuesTest || Boolean(training.test_type)
        : canShowSingleReport) &&
      (permissions.manage || training.trainer.id === currentUser.id)
    );
  }

  return (
    <React.Fragment>
      <ResponsiveContent renderMobile={<Location />} renderDesktop={null} />

      {displayTestReport() &&
        (training.combined_test_report ? (
          <TransitionReport
            trainingId={training.id}
            hasTests={Boolean(training.test_type)}
          />
        ) : (
          <ReliabilityReport
            trainingId={training.id}
            testType={training.test_type}
          />
        ))}

      <Sessions />

      {displayVirtualTrainingLink() && (
        <Section>
          <Text as="h2" className={sectionTitleClassName}>
            {i18n.ft(messages.virtualTrainingLink)}
          </Text>
          <a
            href={training.virtual_training_link}
            rel="noopener noreferrer"
            target="_blank"
          >
            {training.virtual_training_link}
          </a>
        </Section>
      )}

      {displayDescription() && (
        <Section>
          <Text as="h2" className={sectionTitleClassName}>
            {i18n.ft(messages.description)}
          </Text>
          <RichTextEditor readOnly value={template.description} />
        </Section>
      )}

      {displayNotesSection() && (
        <Section>
          {displayNotesTitle() && (
            <Text as="h2" className={sectionTitleClassName}>
              {training.notes_title}
            </Text>
          )}
          {displayNotesDescription() && (
            <RichTextEditor readOnly value={training.notes} />
          )}
        </Section>
      )}

      <CoursesSection />
      <CertificateSection />
    </React.Fragment>
  );
}

export default Details;
